import formatMessage from 'format-message';
import underscoredCrc32 from 'format-message-generate-id/underscored_crc32';
import type { IntlOptions } from '@jetshop/core/components/ConfigProvider';

interface setupIntlProps extends IntlOptions {
  locale: string;
}

interface createTranslatorProps {
  locale: string;
  fallback: string;
  messages: any;
  fallbackMessages: any;
  options: any;
}

const isServer =
  typeof __IN_SERVER__ !== 'undefined'
    ? __IN_SERVER__
    : typeof window === 'undefined';

// Cache translation files
const cachedTranslationPromises: { [key: string]: Promise<any> } = {};

const createTranslator = ({
  locale,
  fallback,
  messages,
  fallbackMessages,
  options
}: createTranslatorProps) => {
  const translator = isServer ? formatMessage.namespace() : formatMessage;

  // We need to create a shallow copy for messages and fallbackMessages,
  // so B2B doesn't fail due to non-extensible type error
  translator.setup({
    translations: {
      [locale]: { ...messages },
      [fallback]: { ...fallbackMessages }
    },
    locale: [locale, fallback],
    generateId: underscoredCrc32,
    ...options
  });

  return translator;
};

/**
 * Takes a locale, e.g. 'sv-SE' and resolves it to the corresponding key in the translations
 * @param {string} locale Example: `sv-SE`
 * @param {object} translations
 * @return {string} Example: `sv`
 */
export const getLocaleName = (
  locale: string,
  translations: IntlOptions['translations']
) => {
  if (!locale) {
    return null;
  }

  if (locale in translations) {
    return locale;
  }

  const lowerCase = locale.toLowerCase();
  if (lowerCase in translations) {
    return lowerCase;
  }

  const baseLocale = lowerCase.split(/[_-]/)[0];
  if (baseLocale in translations) {
    return baseLocale;
  }

  return null;
};

export const getLocaleWithFallback = ({
  locale,
  defaultLocale,
  translations
}: Partial<setupIntlProps>) => {
  const fallback = getLocaleName(defaultLocale, translations);
  const loc = getLocaleName(locale, translations);

  if (!fallback) {
    throw new Error(
      `Default locale "${defaultLocale}" could not be found in translations`
    );
  }
  return {
    locale: loc || fallback,
    fallback
  };
};

export const loadLocaleMessages = (
  locale: string,
  translations: IntlOptions['translations']
):
  | Promise<any>
  | { locale: string; translator: typeof formatMessage; fallback: string } => {
  if (!isServer) {
    return translations[locale]();
  }
  if (!cachedTranslationPromises[locale]) {
    cachedTranslationPromises[locale] = translations[locale]();
  }

  return cachedTranslationPromises[locale];
};

export const setupIntl = async (config: setupIntlProps) => {
  if (!config.translations) {
    throw new Error('No translation data provided to translation setup.');
  }

  if (!config.defaultLocale) {
    throw new Error('No default locale provided to translation setup.');
  }

  const translations = config.translations;
  const options = config.options || {};

  const { locale, fallback } = getLocaleWithFallback({
    locale: config.locale,
    defaultLocale: config.defaultLocale,
    translations
  });

  const [messages, fallbackMessages] = await Promise.all([
    loadLocaleMessages(locale, translations),
    loadLocaleMessages(fallback, translations)
  ]);

  const translator = createTranslator({
    locale,
    fallback,
    messages,
    fallbackMessages,
    options
  });

  return { locale, translator, fallback };
};
